import React from 'react';
import { Phone, Mail, Clock, MessageSquare, ShieldCheck, Truck, Award, MapPin } from 'lucide-react';
import { STORE_CONFIG } from '../../config/storeConfig';
import { useStoreLogo, HEALTH_STORE_FALLBACK_LOGO } from '../../constants/branding';

interface FooterProps {
  onNavigate: (pageId: string) => void;
  onOpenPolicyModal?: (policyType: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigate, onOpenPolicyModal }) => {
  const storeLogo = useStoreLogo();
  const handlePolicyClick = (policyType: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (onOpenPolicyModal) {
      onOpenPolicyModal(policyType);
    }
  };

  return (
    <footer id="main-footer" className="bg-[#0F172A] text-slate-300 pt-14 pb-8 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main 4 Columns Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 pb-12 border-b border-slate-800">
          
          {/* Column 1: Health Store Brand */}
          <div className="space-y-4">
            <button
              onClick={() => {
                onNavigate('home');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-left bg-white p-3 rounded-xl inline-block focus:outline-none hover:bg-slate-50 transition-all cursor-pointer shadow-sm"
              aria-label="Health Store Home"
            >
              <img
                src={storeLogo}
                alt="Health Store - Healthy Today, Stronger Tomorrow"
                width="280"
                height="65"
                className="h-12 sm:h-13 w-auto object-contain select-none"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (!target.src.includes('health_store_official_logo')) {
                    target.src = HEALTH_STORE_FALLBACK_LOGO;
                  }
                }}
              />
            </button>

            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
              {STORE_CONFIG.tagline}
            </p>

            {/* Trust Badges */}
            <div className="pt-2 flex flex-wrap gap-2 text-xs">
              <span className="inline-flex items-center gap-1.5 bg-slate-800/80 text-emerald-400 px-2.5 py-1 rounded-md border border-slate-700 text-[11px] font-semibold">
                <ShieldCheck className="w-3.5 h-3.5" />
                100% Genuine
              </span>
              <span className="inline-flex items-center gap-1.5 bg-slate-800/80 text-amber-400 px-2.5 py-1 rounded-md border border-slate-700 text-[11px] font-semibold">
                <Award className="w-3.5 h-3.5" />
                Halal Certified
              </span>
              <span className="inline-flex items-center gap-1.5 bg-slate-800/80 text-blue-400 px-2.5 py-1 rounded-md border border-slate-700 text-[11px] font-semibold">
                <Truck className="w-3.5 h-3.5" />
                Cash on Delivery
              </span>
            </div>
          </div>

          {/* Column 2: SHOP */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">
              SHOP
            </h3>
            <ul className="space-y-2.5 text-xs sm:text-sm">
              <li>
                <button
                  onClick={() => onNavigate('home')}
                  className="hover:text-white transition-colors hover:underline text-left"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('shop-all')}
                  className="hover:text-white transition-colors hover:underline text-left"
                >
                  Shop All Products
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('supplements')}
                  className="hover:text-white transition-colors hover:underline text-left"
                >
                  Supplements
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('bundles')}
                  className="hover:text-white transition-colors hover:underline text-left flex items-center gap-1.5"
                >
                  <span>Bundle Deals</span>
                  <span className="bg-rose-600 text-white text-[9px] px-1.5 py-0.2 rounded-full font-extrabold uppercase">
                    Save
                  </span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('track-order')}
                  className="hover:text-white transition-colors hover:underline text-left"
                >
                  Track Order
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: CUSTOMER SUPPORT */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">
              CUSTOMER SUPPORT
            </h3>
            <ul className="space-y-2.5 text-xs sm:text-sm">
              <li>
                <a
                  href={`tel:${STORE_CONFIG.phone}`}
                  className="hover:text-white transition-colors hover:underline"
                >
                  Contact Us
                </a>
              </li>
              <li>
                <button
                  onClick={() => onNavigate('home')}
                  className="hover:text-white transition-colors hover:underline text-left"
                >
                  Frequently Asked Questions (FAQs)
                </button>
              </li>
              <li>
                <button
                  onClick={(e) => handlePolicyClick('shipping', e)}
                  className="hover:text-white transition-colors hover:underline text-left"
                >
                  Shipping Policy
                </button>
              </li>
              <li>
                <button
                  onClick={(e) => handlePolicyClick('return', e)}
                  className="hover:text-white transition-colors hover:underline text-left"
                >
                  Return & Refund Policy
                </button>
              </li>
              <li>
                <button
                  onClick={(e) => handlePolicyClick('privacy', e)}
                  className="hover:text-white transition-colors hover:underline text-left"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button
                  onClick={(e) => handlePolicyClick('terms', e)}
                  className="hover:text-white transition-colors hover:underline text-left"
                >
                  Terms & Conditions
                </button>
              </li>
            </ul>
          </div>

          {/* Column 4: CONTACT */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">
              CONTACT & ORDERS
            </h3>
            <ul className="space-y-3 text-xs sm:text-sm">
              <li>
                <a
                  href={STORE_CONFIG.getWhatsAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-emerald-400 hover:text-emerald-300 font-semibold"
                >
                  <svg className="w-4 h-4 fill-emerald-400 shrink-0" viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                  </svg>
                  <span>WhatsApp: {STORE_CONFIG.whatsappDisplay}</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${STORE_CONFIG.phone}`}
                  className="flex items-center gap-2.5 hover:text-white transition-colors"
                >
                  <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                  <span>Phone: {STORE_CONFIG.phoneDisplay}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${STORE_CONFIG.email}`}
                  className="flex items-center gap-2.5 hover:text-white transition-colors"
                >
                  <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                  <span>Email: {STORE_CONFIG.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-slate-400">
                <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <span>{STORE_CONFIG.address}</span>
              </li>
              <li className="flex items-start gap-2.5 text-slate-400">
                <Clock className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <span>{STORE_CONFIG.businessHours}</span>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Bar: Copyright & Legal Policies */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400 text-center sm:text-left">
          <p>© 2026 Health Store Pakistan. All Rights Reserved.</p>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
            <button
              onClick={(e) => handlePolicyClick('privacy', e)}
              className="hover:text-white transition-colors underline-offset-2 hover:underline"
            >
              Privacy Policy
            </button>
            <button
              onClick={(e) => handlePolicyClick('return', e)}
              className="hover:text-white transition-colors underline-offset-2 hover:underline"
            >
              Refund Policy
            </button>
            <button
              onClick={(e) => handlePolicyClick('terms', e)}
              className="hover:text-white transition-colors underline-offset-2 hover:underline"
            >
              Terms of Service
            </button>
            <button
              onClick={(e) => handlePolicyClick('shipping', e)}
              className="hover:text-white transition-colors underline-offset-2 hover:underline"
            >
              Shipping Policy
            </button>
            <a
              href={`mailto:${STORE_CONFIG.email}`}
              className="hover:text-white transition-colors underline-offset-2 hover:underline"
            >
              Contact Information
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
};
